import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Image, FileText, Sparkles, X, Loader2, PenLine, Coins } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useStudyStreak } from "@/hooks/useStudyStreak";
import { useNavigate } from "react-router-dom";
import ProcessingOptions, { type ProcessingType } from "./ProcessingOptions";
import ProcessingResult from "./ProcessingResult";

type InputMode = "upload" | "write";

const UploadSection = () => {
  const [inputMode, setInputMode] = useState<InputMode>("upload");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [textInput, setTextInput] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<{ text: string; type: ProcessingType } | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { credits, hasCredits, refetch: refetchCredits } = useCredits();
  const { updateStreak } = useStudyStreak();
  const navigate = useNavigate();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, []);

  const handleFile = (file: File) => {
    setUploadedFile(file);
    setShowOptions(false);
    setResult(null);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setPreview(null);
    setTextInput("");
    setShowOptions(false);
    setResult(null);
  };

  const saveToDatabase = async (resultText: string, type: ProcessingType, sourceName: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.from('processed_notes').insert({
        user_id: user.id,
        file_name: sourceName,
        processing_type: type,
        result: resultText,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const handleProcess = async (type: ProcessingType) => {
    const hasContent = inputMode === "upload" ? uploadedFile : textInput.trim();
    if (!hasContent) return;

    if (!user) {
      toast({ 
        title: "Login required", 
        description: "Please log in to process and save your notes.",
      });
      navigate('/auth');
      return;
    }

    // Check credits before processing (display only - actual deduction happens server-side)
    if (!hasCredits) {
      toast({ 
        title: "No credits remaining", 
        description: "Purchase a plan to get more credits.",
        variant: "destructive"
      });
      // Scroll to pricing section
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsProcessing(true);
    try {
      // Handle flashcards separately
      if (type === 'flashcards') {
        let content: string;
        
        if (inputMode === "upload" && uploadedFile) {
          // First extract text from the image
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(uploadedFile);
          });
          
          const { data: extractData, error: extractError } = await supabase.functions.invoke('process-notes', {
            body: { imageBase64: base64, type: 'extract' }
          });
          
          if (extractError) throw extractError;
          if (extractData.error) throw new Error(extractData.error);
          
          content = extractData.result;
        } else {
          content = textInput;
        }

        const title = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Notes Flashcards";
        
        const { data, error } = await supabase.functions.invoke('generate-flashcards', {
          body: { content, title }
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        refetchCredits();
        updateStreak(); // Update study streak
        toast({ 
          title: "Flashcards created!", 
          description: `${data.cardCount} flashcards generated. View them in the Flashcards section.`
        });
        setShowOptions(false);
        navigate('/flashcards');
        return;
      }

      // Handle quiz separately
      if (type === 'quiz') {
        let content: string | undefined;
        let imageBase64: string | undefined;
        
        if (inputMode === "upload" && uploadedFile) {
          imageBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(uploadedFile);
          });
        } else {
          content = textInput;
        }

        const title = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Notes Quiz";
        
        const { data, error } = await supabase.functions.invoke('generate-quiz', {
          body: { content, imageBase64, title }
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        refetchCredits();
        updateStreak(); // Update study streak
        toast({ 
          title: "Quiz created!", 
          description: `${data.questionCount} questions generated. View them in the Quizzes section.`
        });
        setShowOptions(false);
        navigate('/quiz');
        return;
      }

      // Regular processing
      let body: { imageBase64?: string; textContent?: string; type: ProcessingType };

      if (inputMode === "upload" && uploadedFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(uploadedFile);
        });
        body = { imageBase64: base64, type };
      } else {
        body = { textContent: textInput, type };
      }

      const { data, error } = await supabase.functions.invoke('process-notes', {
        body
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setResult({ text: data.result, type });
      setShowOptions(false);
      
      const sourceName = inputMode === "upload" && uploadedFile ? uploadedFile.name : "Written Notes";
      await saveToDatabase(data.result, type, sourceName);
      
      // Refetch credits and update streak after successful processing
      refetchCredits();
      updateStreak();
      
      toast({ title: "Processing complete!", description: "Your notes have been processed and saved." });
    } catch (error: any) {
      console.error('Processing error:', error);
      toast({
        title: "Processing failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const hasInput = inputMode === "upload" ? uploadedFile : textInput.trim().length > 0;

  return (
    <section id="upload" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Upload Your Notes
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Upload an image/PDF of your notes or type them directly. 
            We'll help you transform them with AI.
          </p>
          
          {/* Credits Display */}
          {user && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
              <Coins className="w-4 h-4" />
              <span className="font-medium">{credits} credits remaining</span>
            </div>
          )}
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Mode Toggle */}
          {!result && (
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-xl bg-muted p-1">
                <button
                  onClick={() => { setInputMode("upload"); setShowOptions(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    inputMode === "upload"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
                <button
                  onClick={() => { setInputMode("write"); setShowOptions(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    inputMode === "write"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <PenLine className="w-4 h-4" />
                  Write
                </button>
              </div>
            </div>
          )}

          {result ? (
            <div className="glass rounded-2xl p-6 shadow-card">
              <ProcessingResult 
                result={result.text} 
                type={result.type} 
                onBack={clearUpload} 
              />
            </div>
          ) : inputMode === "upload" ? (
            !uploadedFile ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-2xl p-12 transition-all duration-300 cursor-pointer group ${
                  isDragging
                    ? "border-primary bg-primary/5 scale-[1.02]"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                }`}
              >
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileInput}
                  aria-label="Upload notes file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center transition-all duration-300 ${
                    isDragging ? "gradient-primary animate-pulse-glow" : "bg-primary/10 group-hover:bg-primary/20"
                  }`}>
                    <Upload className={`w-8 h-8 ${isDragging ? "text-primary-foreground" : "text-primary"}`} />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {isDragging ? "Drop your notes here!" : "Drag & drop your notes"}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    or click to browse files
                  </p>
                  
                  <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Image className="w-4 h-4" />
                      PNG, JPG
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      PDF
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="glass rounded-2xl p-6 shadow-card">
                <div className="flex items-start gap-4 mb-6">
                  {preview && (
                    <img 
                      src={preview} 
                      alt="Uploaded notes" 
                      className="w-24 h-24 object-cover rounded-lg border border-border"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-foreground truncate">
                        {uploadedFile.name}
                      </h3>
                      <button
                        onClick={clearUpload}
                        className="p-1 hover:bg-muted rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-muted-foreground" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                {!showOptions ? (
                  <Button 
                    variant="hero" 
                    className="w-full"
                    onClick={() => setShowOptions(true)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Process with AI
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-center text-muted-foreground">
                      What would you like to do?
                    </p>
                    <ProcessingOptions 
                      onSelect={handleProcess} 
                      disabled={isProcessing}
                    />
                    {isProcessing && (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">Processing your notes...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="glass rounded-2xl p-6 shadow-card">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">
                    Type or paste your notes
                  </label>
                  {textInput && (
                    <button
                      onClick={clearUpload}
                      className="p-1 hover:bg-muted rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                <Textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Enter your notes here... You can paste content from anywhere or type directly."
                  className="min-h-[200px] resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {textInput.length} characters
                </p>
              </div>

              {!showOptions ? (
                <Button 
                  variant="hero" 
                  className="w-full"
                  onClick={() => setShowOptions(true)}
                  disabled={isProcessing || !textInput.trim()}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Process with AI
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-center text-muted-foreground">
                    What would you like to do?
                  </p>
                  <ProcessingOptions 
                    onSelect={handleProcess} 
                    disabled={isProcessing}
                  />
                  {isProcessing && (
                    <div className="flex items-center justify-center gap-2 text-primary">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Processing your notes...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default UploadSection;
